import React, { useEffect, useState } from "react";
import axios from "axios";
import { Camera, X } from "lucide-react";
import { SITE } from "@/constants/testIds";

const API = "https://moduleris-api.isvecli.com/api";

export const Gallery = () => { const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Simple asymmetric bento layout
  const layout = [
    "md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto",
    "aspect-square",
    "aspect-square",
    "aspect-[4/3] md:aspect-square",
    "aspect-[4/3] md:aspect-square",
  ];

  return (
    <section id="gallery" className="py-24 md:py-32 border-b border-neutral-900 bg-[#0a0a0b] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff5a00]/5 blur-[120px] pointer-events-none" />

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-6 h-px bg-[#ff5a00]" />
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#ff5a00]">
                Galeri / 06
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Sahadan <span className="italic font-light text-neutral-400">kareler</span>.
            </h2>
            <p className="mt-4 text-neutral-400 text-sm md:text-base max-w-xl">
              Yıllar içinde imza attığımız işlerden — villa aydınlatmalarından tarihi
              restorasyonlara — kendi kadrajımızdan seçkiler.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono-tech uppercase tracking-[0.28em] text-neutral-400">
            <Camera size={14} className="text-[#ff5a00]" />
            <span>{items.length} kare</span>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="aspect-square bg-neutral-900 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-neutral-500 font-mono-tech text-sm">
            Galeri şu an güncelleniyor.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-fr">
            {items.map((it, i) => (
              <button
                key={it.id}
                data-testid={`gallery-item-${it.id}`}
                onClick={() => setLightbox(it)}
                className={`relative group overflow-hidden border border-neutral-900 bg-[#0d0d0e] ${
                  layout[i] || "aspect-square"
                }`}
              >
                <img
                  src={it.image}
                  alt={it.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-left">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-[#ff5a00] mb-1">
                    {it.category}
                  </div>
                  <div className="font-display text-sm md:text-base text-white leading-snug">
                    {it.title}
                  </div>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/50 backdrop-blur border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={13} className="text-white" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center border border-white/20 text-white hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            aria-label="Kapat"
          >
            <X size={18} />
          </button>
          <div
            className="relative max-w-6xl max-h-[85vh] w-full flex flex-col md:flex-row gap-6 items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex-1 w-full">
              <img
                src={lightbox.image}
                alt={lightbox.title}
                className="w-full max-h-[75vh] object-contain"
              />
            </div>
            <div className="md:w-72 shrink-0 border border-neutral-800 bg-[#141415] p-6">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#ff5a00] mb-3">
                {lightbox.category}
              </div>
              <h3 className="font-display text-xl text-white leading-snug">
                {lightbox.title}
              </h3>
              <div className="mt-6 pt-6 border-t border-neutral-800 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                MoDuLerİS · Saha Arşivi
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
